import { ref } from 'vue'
import getSensorData from '../../../../composables/getSensorData'
import * as XLSX from 'xlsx'
import JSZip from 'jszip'
import { saveAs } from 'file-saver'
import getDevices from '../../../../composables/getDevices'

const exportAsCSV = () => {
  const { fetchCompleteSensorLabels, fetchDataForCSV, fetchremovedTimePoints } =
    getSensorData()

  const leftCSVData = ref([])
  const rightCSVData = ref([])
  const { getDeviceById } = getDevices()

  const downloadingRangeSessionsData = ref(false)

  const getCSVdata = async (deviceType, sessionId, userDevices) => {
    const labels = await fetchCompleteSensorLabels(deviceType)
    const sensorLabels = labels.sensors
    const promises = sensorLabels.map(id => fetchDataForCSV(sessionId, id, userDevices))

    try {
      const results = await Promise.all(promises)

      leftCSVData.value = []
      rightCSVData.value = []
      results.forEach(result => {
        if (result.leftSensorData) {
          leftCSVData.value.push(...result.leftSensorData)
        }
        if (result.rightSensorData) {
          rightCSVData.value.push(...result.rightSensorData)
        }
      })
    } catch (err) {
      console.error('Error fetching CSV data:', err)
    }
  }

  const downloadCSV = async function (sessionId, columns, name) {
    const removedTimePoints = await fetchremovedTimePoints(sessionId)
    // Assuming 'Time' column is always the first column in the array
    const timeColumn = columns.find(col => col.name === 'Time')

    // Identifying indexes to remove
    const indexesToRemove = timeColumn.data.reduce((acc, time, index) => {
      if (removedTimePoints.includes(time)) acc.push(index)
      return acc
    }, [])

    // Filtering out the rows in all columns
    const filteredColumns = columns.map(column => {
      return {
        name: column.name,
        data: column.data.filter((_, index) => !indexesToRemove.includes(index))
      }
    })

    downloadXLSX(filteredColumns, undefined, name)
  }

  const downloadCSVForBothSides = async function (sessionId, columns1, columns2, name) {
    const removedTimePoints = await fetchremovedTimePoints(sessionId)

    const timeColumn1 = columns1.find(col => col.name === 'Time')

    // Identifying indexes to remove for both sides
    const indexesToRemove1 = timeColumn1.data.reduce((acc, time, index) => {
      if (removedTimePoints.includes(time)) acc.push(index)
      return acc
    }, [])

    // Filtering out the rows in all columns for both sides
    const filteredColumns1 = columns1.map(column => {
      return {
        name: column.name,
        data: column.data.filter((_, index) => !indexesToRemove1.includes(index))
      }
    })

    const filteredColumns2 = columns2.map(column => {
      return {
        name: column.name,
        data: column.data.filter((_, index) => !indexesToRemove1.includes(index)) // Use indexesToRemove2 if different
      }
    })

    downloadXLSX(filteredColumns1, filteredColumns2, name)
  }

  const saveXLSX = function (worksheets, filename) {
    const wb = XLSX.utils.book_new()
    worksheets.forEach((worksheet) => {
      const ws = XLSX.utils.aoa_to_sheet(worksheet.data)
      XLSX.utils.book_append_sheet(wb, ws, worksheet.name)
    })
    XLSX.writeFile(wb, `${filename}.xlsx`)
  }

  const xlsxmaker = function (columns) {
    const data = []
    const columnNames = columns.map(column => column.name)
    data.push(columnNames)

    const numRows = columns.reduce((max, column) => Math.max(max, column.data.length), 0)
    for (let i = 0; i < numRows; i++) {
      const values = []
      for (const column of columns) {
        if (i < column.data.length) {
          values.push(column.data[i])
        } else {
          values.push('')
        }
      }
      data.push(values)
    }
    return data
  }

  const downloadXLSX = async function (leftColumns, rightColumns, name) {
    if (rightColumns) {
      const leftWorksheetData = xlsxmaker(leftColumns)
      const rightWorksheetData = xlsxmaker(rightColumns)
      const worksheets = [
        { name: 'Left', data: leftWorksheetData },
        { name: 'Right', data: rightWorksheetData }
      ]
      saveXLSX(worksheets, name)
    } else {
      const leftWorksheetData = xlsxmaker(leftColumns)
      const worksheets = [
        { name: name, data: leftWorksheetData }
      ]
      saveXLSX(worksheets, name)
    }
  }

  const downloadSessionsAsZip = async (sessions, userDevices) => {
    try {
      downloadingRangeSessionsData.value = true
      const zip = new JSZip()
      for (const session of sessions) {
        const sessionId = session.id
        const removedTimePoints = await fetchremovedTimePoints(sessionId)
        const sessionDeviceIds = session.devices ? session.devices : []
        if (!sessionDeviceIds.length) {
          console.log('No devices found for session:', sessionId)
          continue
        }
        const sessionDevice = await getDeviceById(sessionDeviceIds[0])
        const deviceType = sessionDevice.deviceType
        const leftData = []
        const rightData = []

        const labels = await fetchCompleteSensorLabels(deviceType)
        if (!labels || !labels.sensors) {
          continue
        }
        const sensorLabels = labels.sensors
        const promises = sensorLabels.map(id => fetchDataForCSV(sessionId, id, userDevices))

        try {
          const res = await Promise.all(promises)
          let timeColumnData = []
          if (!res.length) {
            continue
          }
          // Find the 'Time' column in leftSensorData
          if (res[0].leftSensorData.length) {
            timeColumnData = res[0].leftSensorData[0].data
          } else if (res[0].rightSensorData.length) {
            timeColumnData = res[0].rightSensorData[0].data
          }

          // Determine the indices to remove based on removedTimePoints
          const removedIndices = timeColumnData.reduce((indices, timePoint, index) => {
            if (removedTimePoints.includes(timePoint)) {
              indices.push(index)
            }
            return indices
          }, [])

          res.forEach(result => {
            if (result.leftSensorData.length) {
              result.leftSensorData.forEach(column => {
                column.data = column.data.filter((_, index) => !removedIndices.includes(index))
              })
              leftData.push(...result.leftSensorData)
            }
            if (result.rightSensorData.length) {
              result.rightSensorData.forEach(column => {
                column.data = column.data.filter((_, index) => !removedIndices.includes(index))
              })
              rightData.push(...result.rightSensorData)
            }
          })
        } catch (err) {
          console.error('Error while processing session:', err)
        }

        // Convert data to Excel format
        const leftWorksheetData = xlsxmaker(leftData)
        const rightWorksheetData = xlsxmaker(rightData)

        const wb = XLSX.utils.book_new()
        XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(leftWorksheetData), 'Left')
        XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(rightWorksheetData), 'Right')

        // Generate binary string
        const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'binary' })

        // Add to ZIP
        zip.file(`${session.index}-${sessionId}.xlsx`, wbout, { binary: true })
      }

      // Generate ZIP and trigger download
      await zip.generateAsync({ type: 'blob' })
        .then(function (blob) {
          saveAs(blob, 'session_data.zip')
        })
    } catch (error) {
      console.error('Error while downloading session data:', error)
    } finally {
      downloadingRangeSessionsData.value = false
    }
  }

  return { leftCSVData, rightCSVData, getCSVdata, downloadCSV, downloadCSVForBothSides, downloadSessionsAsZip, downloadingRangeSessionsData }
}

export default exportAsCSV
